import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus, Search, Mail, Phone, Download } from 'lucide-react';
import { fetchCustomers } from '../services/api';

const Customers = () => {
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCustomers();
  }, [location]);

  useEffect(() => {
    filterCustomers();
  }, [customers, searchTerm]);

  const loadCustomers = async () => {
    try {
      const data = await fetchCustomers();
      setCustomers(data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterCustomers = () => {
    if (!searchTerm) {
      setFilteredCustomers(customers);
      return;
    }

    const filtered = customers.filter(customer =>
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm)
    );

    setFilteredCustomers(filtered);
  };

  const downloadCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Total Orders', 'Total Spent'];
    const csvContent = [
      headers.join(','),
      ...filteredCustomers.map(c => [
        c.id,
        `"${c.name}"`,
        c.email,
        `"${c.phone}"`,
        c.total_orders || 0,
        c.total_spent || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading customers...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Customers</h1>
        <div className="flex gap-2">
          <button 
            onClick={downloadCSV}
            className="btn-secondary flex items-center gap-2"
          >
            <Download size={20} />
            Export CSV
          </button>
          <Link to="/customers/new" className="btn-primary flex items-center gap-2">
            <Plus size={20} />
            Add Customer
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search customers by name, email, or phone..."
            className="input-field pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Customers Table (Excel Form) */}
      <div className="card overflow-x-auto p-0 border border-gray-300">
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No customers found. Click "Add Customer" to add your first customer.
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-200 border-b border-gray-300">
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">#</th>
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Name</th>
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Email</th>
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Phone</th>
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Orders</th>
                <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Total Spent</th>
                <th className="text-left py-2 px-4 font-bold text-xs uppercase tracking-wider text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer, index) => (
                <tr key={customer.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-500">{index + 1}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 font-medium text-sm">
                    <Link to={`/customers/${customer.id}`} className="text-primary-600 hover:underline">
                      {customer.name}
                    </Link>
                  </td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{customer.email}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{customer.phone}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600 text-center">{customer.total_orders || 0}</td>
                  <td className="py-1.5 px-4 border-r border-gray-200 text-sm font-semibold text-gray-700 text-right">
                    ${Number(customer.total_spent || 0).toFixed(2)}
                  </td>
                  <td className="py-1.5 px-4 text-sm">
                    <div className="flex gap-3">
                      <Link to={`/customers/${customer.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                        View
                      </Link>
                      <Link to={`/customers/${customer.id}/edit`} className="text-gray-600 hover:text-gray-800 font-medium">
                        Edit
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Customers;
